/**
 * O PERÍODO do Início — mês, semestre ou ano — e as contas que dependem dele.
 *
 * Os dois cards de produção ("Produção da equipe" e "Ticket médio") passaram a
 * responder pela mesma janela de tempo, trocada em qualquer um dos dois. Esta
 * é a única definição de o que cada palavra significa, para os dois cards
 * contarem a MESMA história:
 *
 *   mês      → dia 1 do mês corrente até o fim dele
 *   semestre → jan–jun ou jul–dez, decidido pela data de hoje (sem escolher
 *              semestres passados: o Início fala do presente)
 *   ano      → jan–dez do ano corrente
 *
 * A janela vai até o FIM do período, não até hoje: é ela que diz quais meses
 * aparecem no eixo do gráfico, e mês futuro aparece no eixo justamente para o
 * gráfico mostrar quanto caminho ainda falta. Quem precisa cortar em "hoje"
 * (o acumulado, o ritmo) pede isso explicitamente.
 *
 * Tudo aqui é FUNÇÃO PURA de (vendas por mês, hoje) — é o que permite testar
 * "16/09/2026 cai no 2º semestre" sem montar tela nem banco.
 */

export type Periodo = 'mes' | 'semestre' | 'ano';

export type Janela = {
  periodo: Periodo;
  ano: number;
  /** 1 ou 2 — o semestre que contém a janela (o mês de março é do 1º). */
  semestre: 1 | 2;
  /** Meses da janela em ordem, 'YYYY-MM'. Inclui os que ainda não chegaram. */
  meses: string[];
  /** Primeiro dia da janela, 'YYYY-MM-DD'. */
  de: string;
  /** Último dia da janela — o fim do PERÍODO, não hoje. */
  ate: string;
};

/**
 * Vendas confirmadas somadas por mês ('YYYY-MM'), sob a régua do dashboard
 * (contempladas fora — ver useDadosInicio). É a base de todo período: somar
 * meses é mais barato e mais consistente do que refazer a conta por período.
 */
export type VendasPorMes = Record<string, { valor: number; count: number }>;

/** Um mês no eixo dos gráficos de período. */
export type PontoMes = {
  /** 'YYYY-MM' — é o que permite clicar no mês e cair nele em Vendas. */
  chave: string;
  label: string;
  valor: number;
  count: number;
  /**
   * Produção somada do início do período até este mês. NULL no mês que ainda
   * não chegou: zero desenharia a linha caindo ao chão em outubro, dizendo
   * que a equipe desaprendeu a vender.
   */
  acumulado: number | null;
};

const chaveMes = (ano: number, mes: number) => `${ano}-${String(mes).padStart(2, '0')}`;

/**
 * 'YYYY-MM-DD' no fuso LOCAL.
 *
 * `new Date(...).toISOString().slice(0,10)` converte para UTC antes de cortar:
 * a leste de Greenwich o dia 1 vira o último dia do mês anterior, e o filtro
 * de Vendas abriria no mês errado.
 */
const dataISO = (ano: number, mes: number, dia: number) =>
  `${ano}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;

const ultimoDiaDoMes = (ano: number, mes: number) => new Date(ano, mes, 0).getDate();

const nomeMesLongo = (ano: number, mes: number) =>
  new Date(ano, mes - 1, 1).toLocaleDateString('pt-BR', { month: 'long' });

const nomeMesCurto = (ano: number, mes: number) =>
  new Date(ano, mes - 1, 1).toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '');

const montar = (periodo: Periodo, ano: number, primeiro: number, ultimo: number): Janela => {
  const meses: string[] = [];
  for (let m = primeiro; m <= ultimo; m++) meses.push(chaveMes(ano, m));
  return {
    periodo,
    ano,
    semestre: primeiro >= 7 ? 2 : 1,
    meses,
    de: dataISO(ano, primeiro, 1),
    ate: dataISO(ano, ultimo, ultimoDiaDoMes(ano, ultimo)),
  };
};

/** O primeiro mês da janela, 1–12. */
export const primeiroMes = (janela: Janela) => Number(janela.meses[0].slice(5, 7));

/** A janela vigente HOJE — sempre o presente, nunca um período passado. */
export const janelaDoPeriodo = (periodo: Periodo, hoje: Date = new Date()): Janela => {
  const ano = hoje.getFullYear();
  const mes = hoje.getMonth() + 1;
  if (periodo === 'mes') return montar('mes', ano, mes, mes);
  if (periodo === 'semestre') {
    return mes <= 6 ? montar('semestre', ano, 1, 6) : montar('semestre', ano, 7, 12);
  }
  return montar('ano', ano, 1, 12);
};

/**
 * A janela ANTERIOR de mesmo tamanho — a base honesta da comparação do ticket.
 * Ela atravessa o ano quando precisa: o anterior do 1º semestre é o 2º do ano
 * passado, não o 2º deste (que ainda nem aconteceu).
 */
export const janelaAnterior = (periodo: Periodo, hoje: Date = new Date()): Janela => {
  if (periodo === 'mes') {
    const d = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
    const mes = d.getMonth() + 1;
    return montar('mes', d.getFullYear(), mes, mes);
  }
  if (periodo === 'semestre') {
    return hoje.getMonth() + 1 <= 6
      ? montar('semestre', hoje.getFullYear() - 1, 7, 12)
      : montar('semestre', hoje.getFullYear(), 1, 6);
  }
  return montar('ano', hoje.getFullYear() - 1, 1, 12);
};

/** O título do card de produção — muda com o período E com quem olha. */
export const tituloDoPeriodo = (janela: Janela, escopoProprio: boolean) => {
  const sujeito = escopoProprio ? 'Minha produção' : 'Produção da equipe';
  if (janela.periodo === 'mes') {
    return `${sujeito} ${escopoProprio ? 'de' : 'em'} ${nomeMesLongo(janela.ano, primeiroMes(janela))}`;
  }
  if (janela.periodo === 'semestre') return `${sujeito} no ${janela.semestre}º semestre`;
  return `${sujeito} em ${janela.ano}`;
};

/**
 * Como a janela é citada numa comparação ("vs. 1º sem", "vs. 2025"). O ano só
 * aparece quando a janela NÃO é do ano corrente — "vs. 2º sem" sem mais nada
 * seria o semestre que ainda vai acontecer.
 */
export const rotuloDaJanela = (janela: Janela, hoje: Date = new Date()) => {
  if (janela.periodo === 'mes') return nomeMesLongo(janela.ano, primeiroMes(janela));
  if (janela.periodo === 'ano') return String(janela.ano);
  const sufixo = janela.ano === hoje.getFullYear() ? '' : ` de ${janela.ano}`;
  return `${janela.semestre}º sem${sufixo}`;
};

/** "no mês" / "no semestre" / "no ano" — o substantivo solto, para frases. */
export const nomeDoPeriodo = (periodo: Periodo) =>
  periodo === 'mes' ? 'mês' : periodo === 'semestre' ? 'semestre' : 'ano';

/** Total vendido e cotas na janela. Mês sem venda simplesmente não soma. */
export const somaDoPeriodo = (vendas: VendasPorMes, janela: Janela) =>
  janela.meses.reduce(
    (acc, chave) => {
      const m = vendas[chave];
      return m ? { valor: acc.valor + m.valor, count: acc.count + m.count } : acc;
    },
    { valor: 0, count: 0 },
  );

/**
 * Ticket do período: TOTAL VENDIDO ÷ COTAS do período inteiro.
 *
 * Não é a média dos tickets mensais — um mês com uma cota de R$ 300 mil pesaria
 * o mesmo que um mês com trinta cotas, e a média mentiria para cima.
 */
export const ticketDoPeriodo = (vendas: VendasPorMes, janela: Janela) => {
  const { valor, count } = somaDoPeriodo(vendas, janela);
  return count > 0 ? valor / count : null;
};

/** O mês corrente, 'YYYY-MM' — a fronteira entre o que aconteceu e o que não. */
const mesDeHoje = (hoje: Date) => chaveMes(hoje.getFullYear(), hoje.getMonth() + 1);

/**
 * A curva do período: produção ACUMULADA mês a mês, parando no mês corrente.
 * Nunca desce, porque acumulado não desce — e os meses que faltam continuam no
 * eixo, sem ponto.
 */
export const serieAcumulada = (
  vendas: VendasPorMes,
  janela: Janela,
  hoje: Date = new Date(),
): PontoMes[] => {
  const atual = mesDeHoje(hoje);
  let acumulado = 0;
  return janela.meses.map((chave) => {
    const m = vendas[chave] ?? { valor: 0, count: 0 };
    const futuro = chave > atual;
    if (!futuro) acumulado += m.valor;
    return {
      chave,
      label: nomeMesCurto(Number(chave.slice(0, 4)), Number(chave.slice(5, 7))),
      valor: m.valor,
      count: m.count,
      acumulado: futuro ? null : acumulado,
    };
  });
};

/**
 * O ticket mês a mês da janela. Mês sem venda e mês futuro entram como NULL,
 * não como zero: "ticket de R$ 0" é uma afirmação sobre contratos que não
 * existiram.
 */
export const serieTicketMensal = (
  vendas: VendasPorMes,
  janela: Janela,
  hoje: Date = new Date(),
): { chave: string; label: string; ticket: number | null }[] => {
  const atual = mesDeHoje(hoje);
  return janela.meses.map((chave) => {
    const m = vendas[chave];
    const futuro = chave > atual;
    return {
      chave,
      label: nomeMesCurto(Number(chave.slice(0, 4)), Number(chave.slice(5, 7))),
      ticket: futuro || !m || m.count === 0 ? null : m.valor / m.count,
    };
  });
};

const DIA_MS = 86_400_000;

/** Dias inteiros de `a` até `b`, contando os dois. Round absorve o horário de verão. */
const diasEntre = (a: Date, b: Date) =>
  Math.round((b.getTime() - a.getTime()) / DIA_MS) + 1;

/** Quanto do período já passou — a régua do "ritmo fecha em X%". */
export const diasDoPeriodo = (janela: Janela, hoje: Date = new Date()) => {
  const inicio = new Date(janela.ano, primeiroMes(janela) - 1, 1);
  const ultimo = Number(janela.meses[janela.meses.length - 1].slice(5, 7));
  const fim = new Date(janela.ano, ultimo - 1, ultimoDiaDoMes(janela.ano, ultimo));
  const hojeZerado = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
  const totais = diasEntre(inicio, fim);
  const decorridos = Math.min(totais, Math.max(0, diasEntre(inicio, hojeZerado)));
  return { decorridos, totais };
};

/**
 * Projeção de fechamento em % do alvo, no ritmo atual.
 *
 * Null no primeiro dia do período, como no mês: um dia de produção projetado
 * sobre seis meses é adivinhação, não previsão.
 */
export const ritmoDoPeriodo = (
  realizado: number,
  alvo: number,
  dias: { decorridos: number; totais: number },
) =>
  dias.decorridos >= 2 && alvo > 0
    ? Math.round(((realizado / dias.decorridos) * dias.totais * 100) / alvo)
    : null;
